import { ArrowClockwise, Pulse } from '@phosphor-icons/react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEvents } from '../../api/events';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './EventsDebugPage.module.css';

interface BqEvent {
	event_type?: string;
	event_name?: string;
	url?: string;
	element_text?: string;
	target_url?: string;
	form_id?: string;
	scroll_percent?: number;
	server_timestamp?: string;
	visitor_id?: string;
	session_id?: string;
	[key: string]: unknown;
}

/** BQ returns some fields as { value: "..." } objects. Unwrap to plain string. */
function unwrap(val: unknown): string | null {
	if (val == null) return null;
	if (typeof val === 'string') return val;
	if (typeof val === 'object' && 'value' in (val as Record<string, unknown>)) {
		return String((val as Record<string, unknown>).value);
	}
	return String(val);
}

function getEventType(ev: BqEvent): string {
	return ev.event_type ?? 'unknown';
}

function getEventSummary(ev: BqEvent): string {
	const url = unwrap(ev.url);
	if (url) {
		try {
			return new URL(url).pathname;
		} catch {
			return url;
		}
	}
	const name = unwrap(ev.event_name);
	if (name) return name;
	const text = unwrap(ev.element_text);
	if (text) return text;
	const target = unwrap(ev.target_url);
	if (target) return target;
	const form = unwrap(ev.form_id);
	if (form) return form;
	if (ev.scroll_percent != null) return `${ev.scroll_percent}%`;
	return '';
}

const EVENT_TYPES = ['pageview', 'interaction', 'custom'] as const;

export function EventsDebugPage() {
	const { id: projectId = '' } = useParams<{ id: string }>();
	const breadcrumbs = useProjectBreadcrumbs('Events');

	const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
	const [typeFilter, setTypeFilter] = useState<string>('all');

	const activeType = typeFilter === 'all' ? undefined : typeFilter;
	const {
		data: events = [],
		isLoading,
		error,
		refetch,
		isFetching,
	} = useEvents(projectId, activeType);

	const columns = [
		{ key: 'type', label: 'Type' },
		{ key: 'summary', label: 'Detail' },
		{ key: 'visitor', label: 'Visitor' },
		{ key: 'time', label: 'Time' },
	];

	return (
		<div>
			<PageHeader
				title="Events"
				breadcrumbs={breadcrumbs}
				actions={
					<div className={styles.headerActions}>
						<Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
							<ArrowClockwise size={14} weight="bold" />
							{isFetching ? 'Refreshing…' : 'Refresh'}
						</Button>
						<a href="/debug/playground" target="_blank" rel="noopener noreferrer">
							<Button variant="primary">Open Playground</Button>
						</a>
					</div>
				}
			/>

			<div className={shared.content}>
				{error && (
					<Card>
						<p className={shared.errorMessage}>
							{error instanceof Error ? error.message : 'Failed to load events.'}
						</p>
					</Card>
				)}

				<div className={styles.toolbar}>
					<span className={styles.eventCount}>
						{isLoading ? 'Loading…' : `${events.length} event${events.length !== 1 ? 's' : ''}`}
					</span>
					<select
						className={styles.typeFilter}
						value={typeFilter}
						onChange={(e) => {
							setTypeFilter(e.target.value);
							setExpandedIdx(null);
						}}
					>
						<option value="all">All types</option>
						{EVENT_TYPES.map((t) => (
							<option key={t} value={t}>
								{t}
							</option>
						))}
					</select>
				</div>

				<Card>
					<Table>
						<Table.Header columns={columns} />
						<Table.Body>
							{!isLoading && events.length === 0 ? (
								<tr>
									<td colSpan={4}>
										<EmptyState
											icon={Pulse}
											title="No events yet"
											description="Events captured by the SDK will appear here. Open the Playground to start generating events, then click Refresh."
										/>
									</td>
								</tr>
							) : (
								(events as BqEvent[]).map((ev, i) => {
									const isExpanded = expandedIdx === i;
									return (
										<>
											<Table.Row
												key={`row-${
													// biome-ignore lint/suspicious/noArrayIndexKey: display-only
													i
												}`}
												onClick={() => setExpandedIdx(isExpanded ? null : i)}
											>
												<Table.Cell>
													<span className={`${styles.typeBadge} ${styles[getEventType(ev)] ?? ''}`}>
														{getEventType(ev)}
													</span>
												</Table.Cell>
												<Table.Cell>
													<span className={styles.summary}>{getEventSummary(ev)}</span>
												</Table.Cell>
												<Table.Cell mono>
													{ev.visitor_id ? String(ev.visitor_id).slice(0, 8) : '-'}
												</Table.Cell>
												<Table.Cell mono>
													{(() => {
														const ts = unwrap(ev.server_timestamp);
														return ts ? formatRelativeTime(ts) : '-';
													})()}
												</Table.Cell>
											</Table.Row>
											{isExpanded && (
												<tr
													key={`detail-${
														// biome-ignore lint/suspicious/noArrayIndexKey: display-only
														i
													}`}
												>
													<td colSpan={4} className={styles.detailCell}>
														<pre className={styles.detailPre}>{JSON.stringify(ev, null, 2)}</pre>
													</td>
												</tr>
											)}
										</>
									);
								})
							)}
						</Table.Body>
					</Table>
				</Card>
			</div>
		</div>
	);
}
